import { useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const article = useMemo(() => articles.find((a) => a.id === Number(id)), [id]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex flex-col items-center justify-center pt-16 gap-4">
          <p className="text-foreground-500 text-lg">文章不存在</p>
          <button
            onClick={() => navigate('/')}
            className="px-5 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors"
          >
            返回首页
          </button>
        </div>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}/` },
          { '@type': 'ListItem', position: 2, name: article.category, item: `${window.location.origin}/category/${article.categorySlug}` },
          { '@type': 'ListItem', position: 3, name: article.title },
        ],
      },
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        image: article.image,
        datePublished: article.date,
        author: {
          '@type': 'Person',
          name: article.author,
        },
        publisher: {
          '@type': 'Organization',
          name: '亚洲专区',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${window.location.origin}/article/${article.id}`,
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-24 pb-10">
        <div className="max-w-[800px] mx-auto px-4 md:px-6 lg:px-8">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: article.category, path: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <article className="mt-8">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors"
                >
                  {article.category}
                </Link>
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400">{article.readTime}</span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex items-center gap-3">
                <img
                  src={article.authorAvatar}
                  alt={article.author}
                  className="w-9 h-9 rounded-full object-cover"
                />
                <div>
                  <span className="text-sm font-medium text-foreground-700">{article.author}</span>
                  <div className="text-xs text-foreground-400">作者</div>
                </div>
              </div>
            </header>

            <figure className="mb-8 rounded-lg overflow-hidden">
              <img
                src={article.image}
                alt={article.imageAlt}
                className="w-full aspect-[16/9] object-cover object-top"
                title={article.imageAlt}
              />
            </figure>

            <div
              className="prose-custom text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-2 mb-6">
                <span className="text-sm text-foreground-500 font-medium whitespace-nowrap">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 border border-background-200"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <i className="ri-arrow-left-line"></i> 返回{article.category}
                </Link>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-400">
                    <i className="ri-eye-line mr-1"></i>
                    {article.views.toLocaleString()} 次阅读
                  </span>
                </div>
              </div>
            </div>
          </article>

          {category && (
            <div className="mt-16">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                {articles
                  .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
                  .slice(0, 2)
                  .map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.id}`}
                      className="group flex gap-4 p-4 rounded-lg bg-background-100 hover:bg-background-200/70 transition-colors"
                    >
                      <img
                        src={related.image}
                        alt={related.imageAlt}
                        className="w-24 h-24 rounded-lg object-cover object-top shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
                          {related.title}
                        </h3>
                        <p className="text-xs text-foreground-500 line-clamp-2 mb-2">
                          {related.excerpt}
                        </p>
                        <span className="text-xs text-foreground-400">{related.readTime}</span>
                      </div>
                    </Link>
                  ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <style>{`
        .prose-custom h2 {
          font-family: var(--font-heading);
          font-size: 1.375rem;
          font-weight: 700;
          color: oklch(var(--foreground-900));
          margin-top: 2rem;
          margin-bottom: 0.75rem;
        }
        .prose-custom h3 {
          font-family: var(--font-heading);
          font-size: 1.125rem;
          font-weight: 600;
          color: oklch(var(--foreground-800));
          margin-top: 1.5rem;
          margin-bottom: 0.5rem;
        }
        .prose-custom p {
          font-size: 0.9375rem;
          line-height: 1.85;
          color: oklch(var(--foreground-700));
          margin-bottom: 1.25rem;
        }
        .prose-custom strong {
          color: oklch(var(--foreground-900));
          font-weight: 600;
        }
      `}</style>
      <Footer />
    </>
  );
}